import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Chrome, LockKeyhole, MapPin, Shield } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/api";

const DEFAULT_GOOGLE_CLIENT_ID = "153737396638-3bfv714ie1os1t8b01qma5kop7ekgjf9.apps.googleusercontent.com";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || DEFAULT_GOOGLE_CLIENT_ID;

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/dashboard";
  const isLogoutRedirect = searchParams.get("logout") === "1";
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [isChecking, setIsChecking] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [googleMessage, setGoogleMessage] = useState("");
  const [isGoogleButtonReady, setIsGoogleButtonReady] = useState(false);
  const [googleRenderAttempt, setGoogleRenderAttempt] = useState(0);
  const googleButtonRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      if (isLogoutRedirect) {
        setIsChecking(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`);
        if (isMounted) {
          if (response.data?.role === "admin" || response.data?.is_approved) {
            navigate(redirectTo, { replace: true });
            return;
          }
          navigate("/pending-approval", { replace: true });
        }
      } catch {
        // not logged in
      } finally {
        if (isMounted) {
          setIsChecking(false);
        }
      }
    };

    checkAuth();
    return () => {
      isMounted = false;
    };
  }, [navigate, redirectTo, isLogoutRedirect]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      setGoogleMessage("Google-Login ist noch nicht konfiguriert.");
      return undefined;
    }

    let isCancelled = false;
    const scriptId = "google-identity-service";
    let renderCheckTimeout;

    const renderGoogleButton = () => {
      if (isCancelled || !googleButtonRef.current || !window.google?.accounts?.id) {
        return;
      }

      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (response) => {
            if (!response?.credential) {
              toast.error("Google-Anmeldung wurde nicht übergeben");
              return;
            }

            setIsGoogleSubmitting(true);
            try {
              const loginResponse = await axios.post(`${API}/auth/google`, {
                credential: response.credential,
              });

              if (loginResponse.data?.role === "admin" || loginResponse.data?.is_approved) {
                toast.success("Google-Login erfolgreich");
                navigate(redirectTo, { replace: true });
              } else {
                toast.success("Anmeldung gespeichert. Freigabe steht noch aus.");
                navigate("/pending-approval", { replace: true });
              }
            } catch (error) {
              toast.error(error.response?.data?.detail || "Google-Login fehlgeschlagen");
            } finally {
              setIsGoogleSubmitting(false);
            }
          },
        });

        googleButtonRef.current.innerHTML = "";
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "signin_with",
          shape: "pill",
          width: Math.max(280, Math.min(360, googleButtonRef.current.offsetWidth || 320)),
        });
        setGoogleMessage("");
        setIsGoogleButtonReady(false);

        window.clearTimeout(renderCheckTimeout);
        renderCheckTimeout = window.setTimeout(() => {
          if (!isCancelled && googleButtonRef.current && googleButtonRef.current.childElementCount === 0) {
            setGoogleMessage(
              "Google-Button wurde nicht geladen. Bitte Google-OAuth-Client, Browser-Konsole und Cache prüfen."
            );
            setIsGoogleButtonReady(false);
          } else if (!isCancelled) {
            setIsGoogleButtonReady(true);
          }
        }, 1500);
      } catch (error) {
        setGoogleMessage(`Google-Button konnte nicht initialisiert werden: ${error.message}`);
        setIsGoogleButtonReady(false);
      }
    };

    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      if (window.google?.accounts?.id) {
        renderGoogleButton();
      } else {
        existingScript.addEventListener("load", renderGoogleButton, { once: true });
        existingScript.addEventListener(
          "error",
          () => {
            if (!isCancelled) {
              setGoogleMessage("Google-Skript konnte nicht geladen werden.");
            }
          },
          { once: true }
        );
      }
      return undefined;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = renderGoogleButton;
    script.onerror = () => {
      if (!isCancelled) {
        setGoogleMessage("Google-Skript konnte nicht geladen werden.");
      }
    };
    document.head.appendChild(script);

    return () => {
      isCancelled = true;
      window.clearTimeout(renderCheckTimeout);
    };
  }, [navigate, redirectTo, googleRenderAttempt]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      toast.success("Admin-Login erfolgreich");
      navigate(response.data?.role === "admin" ? "/admin" : redirectTo, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login fehlgeschlagen");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isChecking) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative bg-[radial-gradient(circle_at_top,_rgba(180,141,0,0.22),_transparent_35%),linear-gradient(135deg,_#0f172a,_#1c1917_65%,_#44403c)]">
      <div className="absolute inset-0 opacity-30 auth-grid" />
      <div className="relative z-10 min-h-screen px-4 py-10 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="mx-auto w-full max-w-6xl"
        >
          <div className="mb-8 text-center lg:text-left">
            <div className="mb-5 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#b48d00] shadow-lg shadow-amber-950/30">
              <MapPin className="h-8 w-8 text-white" />
            </div>
            <h1 className="heading-xl mb-2 text-white">MR-properties</h1>
            <p className="max-w-xl text-stone-300 lg:text-lg">Die Immo-Map von M&amp;R</p>
          </div>

          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <Card className="border border-white/10 bg-white/95 shadow-2xl backdrop-blur">
              <CardHeader className="text-center lg:text-left">
                <CardTitle className="heading-md text-slate-900">Geschützter Zugriff</CardTitle>
                <CardDescription className="text-stone-600">
                  Google-Login für Benutzer, lokaler Login nur als Admin-Notfallzugang
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="rounded-2xl border border-stone-200 bg-stone-50 p-5">
                  <div className="mb-3 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm">
                      <Chrome className="h-5 w-5 text-slate-700" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-slate-900">Google-Anmeldung</p>
                      <p className="text-sm text-stone-500">
                        Neue Benutzer können sich anmelden und werden danach von dir freigegeben.
                      </p>
                    </div>
                  </div>

                  <div className="min-h-11 flex flex-col items-center justify-center gap-3">
                    <div ref={googleButtonRef} className="w-full" />
                    {!isGoogleButtonReady && !googleMessage && (
                      <p className="text-center text-sm text-stone-500">Google-Button wird geladen...</p>
                    )}
                    {googleMessage && (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          setGoogleMessage("");
                          setGoogleRenderAttempt((current) => current + 1);
                        }}
                      >
                        Google-Login erneut laden
                      </Button>
                    )}
                  </div>

                  {(googleMessage || isGoogleSubmitting) && (
                    <p className="mt-3 text-center text-sm text-stone-500">
                      {isGoogleSubmitting ? "Google-Anmeldung wird verarbeitet..." : googleMessage}
                    </p>
                  )}
                </div>

                <div className="grid gap-3 text-sm sm:grid-cols-2">
                  <div className="rounded-xl bg-stone-100 p-3 text-stone-700">
                    <Shield className="mb-2 h-4 w-4 text-[#b48d00]" />
                    Die komplette Kartenroute wird nur nach Login ausgeliefert.
                  </div>
                  <div className="rounded-xl bg-stone-100 p-3 text-stone-700">
                    <LockKeyhole className="mb-2 h-4 w-4 text-[#b48d00]" />
                    Benutzer mit Google-Login brauchen nach der ersten Anmeldung deine Freigabe.
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="w-full max-w-sm border border-white/10 bg-white/90 shadow-xl backdrop-blur">
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-semibold text-slate-900">Admin-Login</CardTitle>
                <CardDescription className="text-xs leading-5 text-stone-500">
                  Nur für technische Administration und Notfälle.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="username" className="text-xs uppercase tracking-[0.12em] text-stone-500">
                      Benutzername
                    </Label>
                    <Input
                      id="username"
                      value={credentials.username}
                      onChange={(event) =>
                        setCredentials((current) => ({ ...current, username: event.target.value }))
                      }
                      autoComplete="username"
                      placeholder="admin"
                      required
                      className="mt-2 h-10"
                    />
                  </div>

                  <div>
                    <Label htmlFor="password" className="text-xs uppercase tracking-[0.12em] text-stone-500">
                      Passwort
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={credentials.password}
                      onChange={(event) =>
                        setCredentials((current) => ({ ...current, password: event.target.value }))
                      }
                      autoComplete="current-password"
                      placeholder="Passwort"
                      required
                      className="mt-2 h-10"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="h-10 w-full bg-slate-900 text-white hover:bg-slate-800"
                    disabled={isSubmitting || isGoogleSubmitting}
                  >
                    <LockKeyhole className="mr-2 h-4 w-4" />
                    {isSubmitting ? "Admin-Anmeldung läuft..." : "Als Admin anmelden"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
